import { jsx } from "panel";
import { SvgIconEl } from "common/components/svg-icon";
import { RowActions } from "common/query-builder/query-entry-section/components/actions";
import { RowLabel } from "common/query-builder/query-entry-section/components/row-label";
import {
  DROP_POSITION,
  EntryPart,
  isMetricEntry,
  RowLabelType
} from "common/query-builder/query-entry-section/types";
import { TooltipEl } from "common/components/tooltip";
import { DisplaySize, Placement } from "common/components/tooltip/types";
import { Visibility, VisibilityEvents } from "common/components/base/stackable";
import { propagateEvent } from "common/util/dom";
import { MetricHeader } from "common/query-builder/query-entry-section/components/metric-header";
export function BaseEntryRowEl({
  el,
  entry,
  isPlaceholder,
  index,
  rowRenderer,
  hideActions = false,
  extraEntryClass = null,
  showLabels = null,
  rowIcon = null
}) {
  var _a, _b;
  const isMetricEntryRow = isMetricEntry(entry);
  const { prefixRenderer, suffixRenderer, placeholders } = el.state;
  const { entryKey } = entry;
  const isActive = el.state.openEntry.index === index;
  const dropAreas = el.helpers.allowDropAreas(index);
  const dropAllowedAbove = dropAreas.has(DROP_POSITION.ABOVE);
  const dropAllowedBelow = dropAreas.has(DROP_POSITION.BELOW);
  const dropAllowedOn = dropAreas.has(DROP_POSITION.ON);
  const isDraggingEnabled = el.helpers.isDragEnabled(index);
  const isDraggableAnywhere = isDraggingEnabled && el.hasNewQBUI();
  const isDragging = !!el.dragAndDrop.draggingEl;
  const isDropzoneEnabled = dropAreas.size > 0 && isDragging;
  const hasNewUI = el.hasNewQBUI();
  const shouldRenderRowActions = !el.hasNewQBUI() && !!el.params.listProperty && index === 0;
  const hasActions = !el.attr(`readonly`) && (!hideActions || shouldRenderRowActions);
  showLabels = showLabels != null ? showLabels : el.attr(`row-label-type`) !== RowLabelType.None;
  const rowActions = shouldRenderRowActions ? el.params.rowActions : /* @__PURE__ */ jsx(RowActions, {
    hasNewUI: el.hasNewQBUI(),
    hasBehavioralFrequencyFilter: el.helpers.hasBehavioralFrequencyFilter(entry),
    overflowControls: el.helpers.getEntryOverflowControls(index),
    overflowControlsContextPanelTransform: el.helpers.overflowControlsContextPanelTransform,
    isEmptyPlaceholder: el.isEmptyPlaceholder(index, entry),
    canDeleteEntry: el.helpers.canDeleteEntry(),
    disabled: el.attr(`disabled`),
    onEventActionClick: el.helpers.onEventActionClick,
    onControlSelect: el.helpers.onControlSelect,
    index,
    actionsRenderer: rowRenderer.actionsRenderer
  });
  const draggingControl = el.helpers.isDraggable() && /* @__PURE__ */ jsx("div", {
    sel: ".mp-query-entry-gripper",
    class: {
      "is-placeholder": isPlaceholder,
      "is-dragging-disabled": !isDraggingEnabled
    },
    on: {
      mousedown: (ev) => el.helpers.onGripperMouseDown(index, ev)
    }
  }, /* @__PURE__ */ jsx(SvgIconEl, {
    attrs: { icon: hasNewUI ? `gripper-xs` : `gripper` }
  }), hasNewUI && !(el.helpers.isDraggingGripperIndex(index) || isDragging) && /* @__PURE__ */ jsx(TooltipEl, {
    attrs: { text: `Drag to move`, placement: Placement.Top, size: DisplaySize.Small }
  }));
  return /* @__PURE__ */ jsx("div", {
    sel: ".mp-query-entry",
    key: entryKey,
    attrs: {
      elref: `entryRow`,
      draggable: String(isDraggingEnabled),
      disabled: el.attr(`disabled`)
    },
    hook: {
      insert: el.helpers.isStagedRow(index) && (() => el.scrollNthRowIntoView(index))
    },
    class: {
      "mp-metric-entry": isMetricEntryRow,
      "is-active": isActive,
      "drop-indicator-above": dropAllowedAbove && el.helpers.isDroppingAbove(index),
      "drop-indicator-below": dropAllowedBelow && el.helpers.isDroppingBelow(index),
      "drop-indicator-on": dropAllowedOn && el.helpers.isDroppingOn(index),
      "indent-drop-indicator": el.helpers.isNestedSection(),
      "is-placeholder": isPlaceholder,
      "is-draggable": el.helpers.isDraggable(),
      "is-draggable-anywhere": isDraggableAnywhere,
      "is-not-a-drop-area": isDragging && !isDropzoneEnabled,
      "has-actions": hasActions,
      "has-labels": Boolean(showLabels || rowIcon),
      "has-filters": isMetricEntryRow ? false : ((_a = entry.filters) == null ? void 0 : _a.length) > 0 || ((_b = placeholders.get(index)) == null ? void 0 : _b.includes(EntryPart.Filters)),
      "is-being-dragged": el.helpers.isDraggingIndex(index),
      [`is-${entry.type}`]: true,
      "is-exclusion-event": el.helpers.isExclusionStepsSection(),
      ...extraEntryClass
    },
    on: {
      [VisibilityEvents[Visibility.Open]]: () => el.update({ hasOpenDropdown: true }),
      [VisibilityEvents[Visibility.Closed]]: () => el.update({ hasOpenDropdown: false }),
      closeContextMenu: (ev) => {
        el.update({ hasOpenDropdown: false });
        propagateEvent(el, ev);
      },
      dragend: el.helpers.onEntryStepDragClear,
      ...isDraggableAnywhere ? { mousedown: (ev) => el.helpers.onGripperMouseDown(index, ev) } : {},
      ...isDraggingEnabled ? {
        dragstart: (ev) => el.helpers.onEntryStepDragStart(ev, index),
        dragenter: (ev) => el.helpers.onEntryStepDragEnter(ev, index)
      } : {},
      ...isDropzoneEnabled ? {
        dragover: (ev) => el.helpers.onEntryStepDragOver(ev, index),
        dragleave: () => el.helpers.onEntryStepDragLeave(),
        drop: (ev) => el.helpers.onEntryStepDrop(ev, index)
      } : {}
    }
  }, hasNewUI && isDropzoneEnabled && /* @__PURE__ */ jsx(null, null, /* @__PURE__ */ jsx("div", {
    sel: ".drop-zone-top .drop-zone",
    class: {
      "is-first": index === 0 && !Number.isInteger(el.attr(`row-index`))
    }
  }), /* @__PURE__ */ jsx("div", {
    sel: ".drop-zone-bottom .drop-zone",
    class: {
      "is-last": index === el.helpers.getEntries().length - 1 && !Number.isInteger(el.attr(`row-index`))
    }
  })), typeof prefixRenderer === `function` && prefixRenderer.call(void 0, { index, entry }) || rowRenderer.prefixRenderer && /* @__PURE__ */ jsx(rowRenderer.prefixRenderer, {
    el,
    index,
    entry
  }), !hasNewUI && draggingControl, showLabels && /* @__PURE__ */ jsx(RowLabel, {
    el,
    index,
    entry,
    isPlaceholder,
    reportName: el.attr(`report-name`)
  }), hasNewUI && rowIcon && /* @__PURE__ */ jsx("div", {
    sel: ".mp-query-entry-icon"
  }, /* @__PURE__ */ jsx(SvgIconEl, {
    attrs: { icon: rowIcon }
  })), /* @__PURE__ */ jsx("div", {
    sel: ".mp-query-entry-body"
  }, isMetricEntryRow && /* @__PURE__ */ jsx(MetricHeader, {
    el,
    index,
    entry
  }), rowRenderer.selectorRenderer && (isMetricEntryRow ? entry.isExpanded : true) && /* @__PURE__ */ jsx("div", {
    sel: ".mp-query-entry-row.for-selector",
    class: { "has-label": showLabels, "is-placeholder": isPlaceholder },
    attrs: { elref: `queryEntryRow` }
  }, /* @__PURE__ */ jsx(rowRenderer.selectorRenderer, {
    rowActions
  }), hasActions && rowActions), rowRenderer.bodyRowRenderer && /* @__PURE__ */ jsx(rowRenderer.bodyRowRenderer, {
    el,
    index
  })), typeof suffixRenderer === `function` && suffixRenderer.call(void 0, { index, entry }) || rowRenderer.suffixRenderer && /* @__PURE__ */ jsx(rowRenderer.suffixRenderer, {
    el,
    index,
    entry
  }), hasNewUI && draggingControl);
}
